import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowDown } from "lucide-react";
import { LOGO } from "../Brand";
import AuroraWave from "../AuroraWave";

const line = {
  hidden: { y: "110%" },
  show: (i) => ({ y: 0, transition: { duration: 1, delay: 0.15 + i * 0.12, ease: [0.19, 1, 0.22, 1] } }),
};

export default function HeroOS({ onExplore, onVision, onTalk }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const logoY = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const logoScale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <section ref={ref} className="relative min-h-[100svh] flex items-center overflow-hidden pt-24" data-testid="hero">
      {/* aurora band behind — the signature wave flowing across the hero */}
      <AuroraWave className="absolute left-[-15%] right-0 top-[42%] h-[420px] w-[130%] pointer-events-none" opacity={0.95} pulse />

      <div className="relative max-w-[1600px] mx-auto px-6 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center w-full">
        {/* Left — text */}
        <motion.div style={{ opacity: fade }} className="lg:col-span-7 order-2 lg:order-1">
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.1, duration: 0.6 }}
            className="flex items-center gap-3 eyebrow mb-8">
            <span className="w-10 h-px bg-[color:var(--gold)]" />
            DEBSUN Technologies
          </motion.div>

          <h1 className="display display-hero text-[clamp(2.6rem,6.2vw,5.6rem)]">
            {["Engineering Practical", "Technologies for a", <span key="s" style={{ color: "var(--gold)" }}>Smarter Tomorrow.</span>].map((l, i) => (
              <span key={i} className="block overflow-hidden">
                <motion.span className="block" variants={line} initial="hidden" animate="show" custom={i}>{l}</motion.span>
              </span>
            ))}
          </h1>

          <motion.p initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.75, duration: 0.8 }}
            className="mt-8 max-w-2xl text-base md:text-lg text-steel-text leading-relaxed">
            DEBSUN Technologies develops practical engineering solutions across smart electronics,
            industrial automation, digital platforms and applied research—connecting hardware,
            software and intelligence to solve real industrial challenges.
          </motion.p>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.1, duration: 0.8 }}
            className="mt-11 flex flex-col xs:flex-row sm:flex-row items-stretch sm:items-center gap-3 sm:gap-5 flex-wrap w-full sm:w-auto">
            <button onClick={onExplore} data-testid="hero-explore-btn"
              className="btn-gold clip-frame-sm px-6 sm:px-7 py-3.5 sm:py-4 text-xs uppercase tracking-[0.2em] font-mono-2 inline-flex items-center justify-center gap-2 w-full sm:w-auto">
              Explore Solutions <ArrowRight size={15} />
            </button>
            <button onClick={onVision} data-testid="hero-products-btn"
              className="btn-outline clip-frame-sm px-6 sm:px-7 py-3.5 sm:py-4 text-xs uppercase tracking-[0.2em] font-mono-2 inline-flex items-center justify-center gap-2 w-full sm:w-auto">
              View Products
            </button>
            <button onClick={onTalk} data-testid="hero-talk-btn"
              className="group inline-flex items-center justify-center sm:justify-start gap-2 font-mono-2 text-xs uppercase tracking-[0.2em] text-muted-foreground hover:text-[color:var(--gold)] transition-colors duration-500 py-3 sm:pl-1 w-full sm:w-auto">
              Talk to Engineering
              <ArrowRight size={13} className="transition-transform duration-500 group-hover:translate-x-1" />
            </button>
          </motion.div>
        </motion.div>

        {/* Right — the DEBSUN logo, aurora flowing through it (brand-board treatment) */}
        <motion.div style={{ opacity: fade }} className="lg:col-span-5 order-1 lg:order-2 relative h-[340px] lg:h-[560px] flex items-center justify-center">
          {/* soft radial glow bed */}
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <div className="w-[80%] h-[60%] rounded-full"
              style={{ background: "radial-gradient(circle, rgba(0,229,212,.16), rgba(138,92,246,.10) 45%, transparent 70%)", filter: "blur(30px)" }} />
          </div>
          {/* aurora passing directly through the logo */}
          <AuroraWave className="absolute left-[-20%] right-[-20%] top-1/2 -translate-y-1/2 h-[240px] w-[140%] pointer-events-none z-10" opacity={0.95} />

          <motion.img
            src={LOGO} alt="DEBSUN Technologies"
            style={{ y: logoY, scale: logoScale }}
            initial={{ opacity: 0, scale: 0.9, filter: "blur(6px)" }}
            animate={{ opacity: 1, scale: 1, filter: "blur(0px)" }}
            transition={{ delay: 0.35, duration: 1.3, ease: [0.19, 1, 0.22, 1] }}
            className="relative z-20 w-[92%] max-w-[480px] drop-shadow-[0_30px_70px_rgba(212,175,55,0.22)]"
          />
        </motion.div>
      </div>

      {/* Signature transition — Intelligence in Motion (reusable DEBSUN signature element) */}
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.7, duration: 1.2 }}
        className="absolute bottom-8 inset-x-0 flex flex-col items-center gap-3" data-testid="hero-signature">
        <div className="relative w-full h-16 flex items-center justify-center overflow-hidden pointer-events-none">
          <AuroraWave className="absolute inset-y-0 left-[-15%] w-[130%] h-full" opacity={0.55} variant="thin" sparkles={false} speed={1.65} />
          <div className="relative z-10 flex items-center gap-4 md:gap-6">
            <span className="w-8 md:w-14 h-px bg-[color:var(--gold)]/50" />
            <span className="font-mono-2 text-[10px] md:text-[11px] tracking-[0.45em] uppercase text-foreground/70">Intelligence in Motion</span>
            <span className="w-8 md:w-14 h-px bg-[color:var(--gold)]/50" />
          </div>
        </div>
        <motion.span animate={{ y: [0, 5, 0] }} transition={{ repeat: Infinity, duration: 3 }}>
          <ArrowDown size={12} className="text-[color:var(--gold)]/60" />
        </motion.span>
      </motion.div>
    </section>
  );
}
